import { useMutation, useQuery } from '@tanstack/react-query';
import {
  type CostOption,
  costTotal,
  DEFAULT_THRESHOLDS,
  formatMoney,
  fromCents,
  type LineType,
  marginOf,
  nextSectionName,
  type NewSectionRequest,
  parseDecimal,
  type QuoteLookups,
  type QuoteResponse,
  quoteTotals,
  type SectionTemplate,
  toCents,
} from '@k-management/shared';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useCan } from '../../auth/AuthProvider.js';
import { DecimalInput } from '../../components/DecimalInput.js';
import { Field } from '../../components/Field.js';
import { ApiError, apiRequest } from '../../lib/api.js';
import { MarginBadge, selectAll } from './CostsView.js';

/** A row of the grid: what is left null follows the lists of the quote. */
interface Row {
  key: number;
  subTypeCode: string;
  description: string;
  unitCode: string;
  quantity: string;
  /** Null: the price of the listino attività for the sub-type and unit. */
  unitPrice: string | null;
  discountPercent: string;
  /** Null: the voice of the listino passività proposed for the sub-type and unit. */
  cost: { code: string | null; unitCost: string } | null;
}

/** The price of the list of the quote: the sub-type first, then the whole type. */
function listedPrice(
  quote: QuoteResponse,
  typeCode: string,
  subTypeCode: string,
  unitCode: string,
): string | null {
  const prices = quote.activityPrices.filter(
    (item) => item.typeCode === typeCode && item.unitCode === unitCode,
  );
  return (
    prices.find((item) => item.subTypeCode === subTypeCode)?.price ??
    prices.find((item) => item.subTypeCode === null)?.price ??
    null
  );
}

/** The cost proposed for a row: a voice in the same unit, else the first one. */
function proposedCost(options: readonly CostOption[] | undefined, unitCode: string) {
  const usable = (options ?? []).filter((option) => option.unitCost !== null);
  return usable.find((option) => option.unitCode === unitCode) ?? usable[0] ?? null;
}

let nextKey = 1;

/**
 * «Compila da listino» (proposal Listini §7, decisions of 01/10/2026): all the
 * sub-types of a type in a grid, with the prices of the lists of the quote and,
 * for who sees them, the costs. Only the rows with a quantity go into the quote,
 * as a new named section; from then on the lines are worked on in the body.
 */
export function FillFromListDialog({
  quote,
  lookups,
  typeCode: initialType,
  onClose,
  onDone,
}: {
  quote: QuoteResponse;
  lookups: QuoteLookups;
  /** The type chosen in «Nuova riga». */
  typeCode: string;
  onClose: () => void;
  onDone: (updated: QuoteResponse) => Promise<void>;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const canDiscount = useCan('preventivatore.discounts');
  const typeCode = initialType;
  const type = lookups.types.find((item) => item.code === typeCode);
  const children = useMemo(
    () => lookups.types.filter((item) => item.parent === typeCode),
    [lookups.types, typeCode],
  );
  const defaultUnit = lookups.units.find((unit) => unit.isDefault)?.code ?? lookups.units[0]?.code;
  const defaultVat =
    lookups.vatRates.find((rate) => rate.isDefault)?.code ?? lookups.vatRates[0]?.code ?? '';

  const newRow = (child: LineType): Row => ({
    key: nextKey++,
    subTypeCode: child.code,
    description: '',
    unitCode: child.unit ?? type?.unit ?? defaultUnit ?? '',
    quantity: '',
    unitPrice: null,
    discountPercent: '',
    cost: null,
  });
  const [rows, setRows] = useState<Row[]>(() => children.map(newRow));
  const [name, setName] = useState(() =>
    nextSectionName(
      type?.description ?? '',
      quote.sections.map((section) => section.name),
    ),
  );
  const [showCosts, setShowCosts] = useState(true);
  const [error, setError] = useState('');
  const [errorRow, setErrorRow] = useState<number | null>(null);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const template = useQuery({
    queryKey: ['quotes', quote.id, 'section-template', typeCode],
    queryFn: () => apiRequest<SectionTemplate>(`/quotes/${quote.id}/sections/template/${typeCode}`),
    enabled: typeCode !== '',
  });
  const costs = template.data?.costs ?? null;
  const withCosts = costs !== null && showCosts;
  const thresholds = quote.costs?.thresholds ?? DEFAULT_THRESHOLDS;

  const change = (key: number, patch: Partial<Row>) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  const move = (index: number, direction: -1 | 1) =>
    setRows((current) => {
      const target = index + direction;
      if (target < 0 || target >= current.length) return current;
      const moved = [...current];
      [moved[index], moved[target]] = [moved[target]!, moved[index]!];
      return moved;
    });

  /** What every row is worth now: price and cost from the lists unless written. */
  const priced = rows.map((row) => {
    const child = children.find((item) => item.code === row.subTypeCode);
    const listPrice = listedPrice(quote, typeCode, row.subTypeCode, row.unitCode);
    const unitPrice = row.unitPrice ?? listPrice ?? '';
    const option = proposedCost(costs?.[row.subTypeCode], row.unitCode);
    const unitCost = row.cost ? row.cost.unitCost : (option?.unitCost ?? '');
    // A cost written equal to its voice is still the voice.
    const costChanged =
      row.cost !== null &&
      (row.cost.code === null ||
        option === null ||
        parseDecimal(row.cost.unitCost) !== parseDecimal(option.unitCost ?? ''));
    const quantity = parseDecimal(row.quantity) ?? 0;
    const filled = quantity > 0;
    const net = filled
      ? quoteTotals([
          {
            quantity: row.quantity,
            unitPrice: parseDecimal(unitPrice) === null ? 0 : unitPrice,
            discountPercent: parseDecimal(row.discountPercent) ?? 0,
            vatCode: defaultVat,
          },
        ]).net
      : '0.00';
    const cost =
      filled && parseDecimal(unitCost) !== null ? costTotal(row.quantity, unitCost) : null;
    return { row, child, listPrice, unitPrice, option, unitCost, costChanged, filled, net, cost };
  });
  const filled = priced.filter((item) => item.filled);
  const sold = filled.reduce((cents, item) => cents + toCents(item.net), 0);
  const cost = filled.reduce((cents, item) => cents + (item.cost ? toCents(item.cost) : 0), 0);

  const save = useMutation({
    mutationFn: (body: NewSectionRequest) =>
      apiRequest<QuoteResponse>(`/quotes/${quote.id}/sections`, { method: 'POST', body }),
    onSuccess: (updated) => onDone(updated),
    onError: (failure: Error) => {
      // «Riga 2» is the second row sent: point at it in the grid.
      const sent = Object.keys(failure instanceof ApiError ? failure.fieldErrors : {})
        .map((key) => /^rows\.(\d+)\./.exec(key)?.[1])
        .find((index) => index !== undefined);
      const item = sent === undefined ? undefined : filled[Number(sent)];
      setErrorRow(item ? item.row.key : null);
      setError(
        item
          ? failure.message.replace(/^Riga \d+: /, `${item.child?.description ?? 'Riga'}: `)
          : failure.message,
      );
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setErrorRow(null);
    if (!name.trim()) {
      setError('Scrivi il nome della sezione');
      return;
    }
    if (filled.length === 0) {
      setError('Scrivi la quantità delle righe da inserire');
      return;
    }
    for (const item of filled) {
      const label = item.child?.description ?? item.row.subTypeCode;
      const problem =
        item.child?.freeText && !item.row.description.trim()
          ? `${label}: scrivi che cosa comprende la riga`
          : parseDecimal(item.unitPrice) === null
            ? `${label}: manca il prezzo`
            : null;
      if (problem) {
        setErrorRow(item.row.key);
        setError(problem);
        return;
      }
    }
    save.mutate({
      name: name.trim(),
      typeCode,
      rows: filled.map((item) => ({
        subTypeCode: item.row.subTypeCode,
        description: item.row.description.trim() || null,
        unitCode: item.row.unitCode,
        quantity: item.row.quantity,
        unitPrice: item.unitPrice,
        discountPercent: canDiscount ? (parseDecimal(item.row.discountPercent) ?? 0) : 0,
        cost:
          costs === null
            ? null
            : item.row.cost
              ? item.row.cost.unitCost.trim() === ''
                ? null
                : item.row.cost
              : item.option
                ? { code: item.option.code, unitCost: item.option.unitCost! }
                : null,
      })),
    });
  };

  const units = (code: string) =>
    lookups.units.find((unit) => unit.code === code)?.description ?? code;

  return (
    <dialog ref={dialogRef} className="dialog fill-dialog" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <h2>Nuove righe: {type?.description ?? typeCode}</h2>
        <p className="muted small">
          Tutte le voci di {type?.description ?? 'questa tipologia'}, con i prezzi del listino{' '}
          <strong>{quote.priceLists.activity.name}</strong>
          {quote.priceLists.cost && (
            <>
              {' '}
              e i costi del listino <strong>{quote.priceLists.cost.name}</strong>
            </>
          )}
          . Vanno nel preventivo solo le righe con la quantità, come una nuova sezione; dopo si
          modificano nel corpo del preventivo.
        </p>
        <div className="row">
          <Field label="Nome della sezione">
            <input value={name} maxLength={120} onChange={(event) => setName(event.target.value)} />
          </Field>
          {costs !== null && (
            <label className="checkbox fill-toggle">
              <input
                type="checkbox"
                checked={showCosts}
                onChange={(event) => setShowCosts(event.target.checked)}
              />
              Mostra costi e margini
            </label>
          )}
        </div>
        <div className="fill-grid-wrap">
          <table className="lines editing fill-grid">
            <thead>
              <tr>
                <th>Voce</th>
                <th>Descrizione per il cliente</th>
                <th>U.M.</th>
                <th className="right">Q.tà</th>
                <th className="right">Prezzo</th>
                {canDiscount && <th className="right">Sconto %</th>}
                <th className="right">Importo</th>
                {withCosts && (
                  <>
                    <th className="right">Costo unit.</th>
                    <th className="right">Costo</th>
                    <th className="right">Margine</th>
                  </>
                )}
                <th aria-label="Azioni" />
              </tr>
            </thead>
            <tbody>
              {priced.map((item, index) => {
                const { row } = item;
                const label = item.child?.description ?? row.subTypeCode;
                const priceChanged =
                  row.unitPrice !== null &&
                  item.listPrice !== null &&
                  parseDecimal(row.unitPrice) !== parseDecimal(item.listPrice);
                const costUnit = item.option?.unitCode;
                return (
                  <tr
                    key={row.key}
                    className={[
                      item.filled ? 'filled' : 'empty',
                      errorRow === row.key ? 'error-row' : '',
                    ].join(' ')}
                  >
                    <td>{label}</td>
                    <td>
                      <input
                        value={row.description}
                        maxLength={300}
                        aria-label={`Descrizione ${label}`}
                        placeholder={item.child?.freeText ? 'Obbligatoria' : ''}
                        onChange={(event) => change(row.key, { description: event.target.value })}
                      />
                    </td>
                    <td>
                      <select
                        value={row.unitCode}
                        aria-label={`Unità ${label}`}
                        onChange={(event) =>
                          // Price and cost still as the lists give them follow the new unit.
                          change(row.key, {
                            unitCode: event.target.value,
                            ...(priceChanged || item.listPrice === null ? {} : { unitPrice: null }),
                            ...(item.costChanged ? {} : { cost: null }),
                          })
                        }
                      >
                        {lookups.units.map((unit) => (
                          <option key={unit.code} value={unit.code}>
                            {unit.description}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="right">
                      <DecimalInput
                        className="number"
                        decimals={[0, 3]}
                        value={row.quantity}
                        aria-label={`Quantità ${label}`}
                        onFocus={selectAll}
                        onChange={(value) => change(row.key, { quantity: value })}
                      />
                    </td>
                    <td className="right">
                      <DecimalInput
                        className="number"
                        decimals={[2, 2]}
                        value={item.unitPrice}
                        aria-label={`Prezzo ${label}`}
                        onFocus={selectAll}
                        onChange={(value) => change(row.key, { unitPrice: value })}
                      />
                      {priceChanged && (
                        <small className="muted block">
                          listino {formatMoney(item.listPrice!)}{' '}
                          <button
                            type="button"
                            className="link small"
                            title="Torna al prezzo di listino"
                            onClick={() => change(row.key, { unitPrice: null })}
                          >
                            ↺
                          </button>
                        </small>
                      )}
                      {item.listPrice === null && row.unitPrice === null && (
                        <small className="muted block">senza listino</small>
                      )}
                    </td>
                    {canDiscount && (
                      <td className="right">
                        <DecimalInput
                          className="number"
                          decimals={[0, 2]}
                          value={row.discountPercent}
                          placeholder="0"
                          aria-label={`Sconto ${label}`}
                          onFocus={selectAll}
                          onChange={(value) => change(row.key, { discountPercent: value })}
                        />
                      </td>
                    )}
                    <td className="right">{item.filled ? formatMoney(item.net) : '—'}</td>
                    {withCosts && (
                      <>
                        <td className="right">
                          <DecimalInput
                            className="number"
                            decimals={[2, 2]}
                            value={item.unitCost}
                            aria-label={`Costo unitario ${label}`}
                            onFocus={selectAll}
                            onChange={(value) =>
                              change(row.key, {
                                cost: { code: item.option?.code ?? null, unitCost: value },
                              })
                            }
                          />
                          <small className="muted block">
                            {item.costChanged
                              ? row.cost?.code
                                ? 'modificato'
                                : 'scritto a mano'
                              : item.option
                                ? `${item.option.description}${costUnit && costUnit !== row.unitCode ? ` · ${units(costUnit)}` : ''}`
                                : 'nessun costo a listino'}
                          </small>
                        </td>
                        <td className="right">{item.cost ? formatMoney(item.cost) : '—'}</td>
                        <td className="right">
                          {item.filled && item.cost ? (
                            <MarginBadge margin={marginOf(item.net, item.cost, thresholds)} />
                          ) : (
                            '—'
                          )}
                        </td>
                      </>
                    )}
                    <td className="row-actions">
                      <button
                        type="button"
                        className="small"
                        aria-label={`Sposta su ${label}`}
                        disabled={index === 0}
                        onClick={() => move(index, -1)}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="small"
                        aria-label={`Sposta giù ${label}`}
                        disabled={index === rows.length - 1}
                        onClick={() => move(index, 1)}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="small"
                        aria-label={`Togli ${label}`}
                        onClick={() =>
                          setRows((current) => current.filter((item) => item.key !== row.key))
                        }
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="fill-footer">
          <label className="fill-add">
            <select
              value=""
              aria-label="Aggiungi riga"
              onChange={(event) => {
                const child = children.find((item) => item.code === event.target.value);
                if (child) setRows((current) => [...current, newRow(child)]);
              }}
            >
              <option value="">+ Aggiungi riga…</option>
              {children.map((child) => (
                <option key={child.code} value={child.code}>
                  {child.description}
                </option>
              ))}
            </select>
          </label>
          <div className="fill-summary">
            <span>
              Righe compilate <strong>{filled.length}</strong>
            </span>
            <span>
              Vendita <strong>{formatMoney(fromCents(sold))}</strong>
            </span>
            {withCosts && (
              <>
                <span>
                  Costo <strong>{formatMoney(fromCents(cost))}</strong>
                </span>
                <span>
                  Margine{' '}
                  {sold > 0 ? (
                    <MarginBadge margin={marginOf(fromCents(sold), fromCents(cost), thresholds)} />
                  ) : (
                    '—'
                  )}
                </span>
              </>
            )}
          </div>
        </div>
        {template.isError && <p className="error">{template.error.message}</p>}
        {error && <p className="error">{error}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button
            type="submit"
            className="primary"
            disabled={save.isPending || filled.length === 0}
          >
            {save.isPending
              ? 'Inserimento…'
              : filled.length === 1
                ? 'Inserisci 1 riga'
                : `Inserisci ${filled.length} righe`}
          </button>
        </div>
      </form>
    </dialog>
  );
}
